import {ReactNode, createContext, useMemo} from 'react';
import {FormAugmentationData} from 'mkt-ssr/lib/forms/types';
import {useRequiredContext} from 'mkt-ssr/lib/contextHelpers';
// eslint-disable-next-line @sail/data-no-cookie-imports
import {LocalStorage, Cookies} from '@stripe-internal/stripe-cookies';
import {useGetPreviousEventId} from 'mkt-ssr/lib/analytics';
import {useLocaleCode} from 'mkt-ssr/lib/globalization';

const FormAugmentationContext = createContext<FormAugmentationData | undefined>(
  undefined,
);

export function useFormAugmentation(): FormAugmentationData {
  return useRequiredContext(FormAugmentationContext);
}

interface FormAugmentationProviderProps {
  children: ReactNode;
}

function getAnalyticsCid() {
  const cookies = new Cookies();
  const localStorage = new LocalStorage();
  const cid = cookies.get('cid');
  const lsid = localStorage.get('lsid');
  return cid || lsid || '';
}

/**
 * This is the only attribution field that does NOT support undefined in Marketo.
 * However, this field supports null which is now set as the default value. Upon
 * discussion with marketing ops, we are updating the field to have the same date
 * imt.js would have set. This change is in compliance with tracking laws as
 * this is just the date of the form submission. We are not updating any UTM params.
 * @returns sets the attribution to current date in PDT if null.
 */
function getFFConversionDate() {
  // This implementation is copied from mkt/SourceAttribution/imt.js
  // @returns current date, approximating PDT, as a string
  const d = new Date();
  d.setTime(d.getTime() + d.getTimezoneOffset() * 60000 - 420 * 60000); // 420 = 7 * 60 (7 hour offset for PDT)
  return `${d.getUTCFullYear()}-${d.getUTCMonth() < 9 ? '0' : ''}${
    d.getUTCMonth() + 1
  }-${d.getUTCDate() < 10 ? '0' : ''}${d.getUTCDate()}`;
}

export function FormAugmentationProvider({
  children,
}: FormAugmentationProviderProps) {
  const previousEventId = useGetPreviousEventId();
  const localeCode = useLocaleCode();
  const augmentationData = useMemo<FormAugmentationData>(() => {
    return {
      // TODO: should we load these from imt.js, or let imt.js mutate our form?
      FF_UTM_Campaign_First_Click__c: 'Undefined',
      FF_UTM_Campaign_Last_Click__c: 'Undefined',
      FF_UTM_Content_First_Click__c: 'Undefined',
      FF_UTM_Content_Last_Click__c: 'Undefined',
      FF_UTM_Medium_First_Click__c: 'Direct',
      FF_UTM_Medium_Last_Click__c: 'Direct',
      FF_UTM_Source_First_Click__c: 'direct',
      FF_UTM_Source_Last_Click__c: 'direct',
      FF_UTM_Term_First_Click__c: 'Undefined',
      FF_UTM_Term_Last_Click__c: 'Undefined',
      FF_Conversion_Page__c: 'Undefined',
      Referrer_URL__c: 'Undefined',
      FF_Conversion_Date__c: getFFConversionDate(),
      analytics_cid: getAnalyticsCid(),
      analytics_event_id: previousEventId || '',
      mostRecentLanguage: localeCode,
    };
  }, [previousEventId, localeCode]);

  return (
    <FormAugmentationContext.Provider value={augmentationData}>
      {children}
    </FormAugmentationContext.Provider>
  );
}
